import { CheckCircle2, Circle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { BodyText, StepCard, SubHeading } from '../../components/ui'
import { AuthorizationUploads, OtrUpload, SignatureUpload } from '../../components/ImplementationUploads'
import { authorizationStatesNeeded } from '../../lib/completion'
import { markClientOnboardingComplete } from '../../lib/onboardingPortalFlags'
import { SUPPORT_EMAIL } from '../../data/mockSeed'
import { AppRoutes } from '../../../helper/appRoutes'

function TaskHeading({ done, children }: { done: boolean; children: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box component="span" sx={{ color: done ? '#059669' : '#94a3b8', display: 'flex' }}>
        {done ? <CheckCircle2 size={18} aria-hidden /> : <Circle size={18} aria-hidden />}
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{children}</Typography>
    </Box>
  )
}

/** Design manual p.12 — Congratulations + "Want to Get Ahead of the Curve?" next steps. */
export function StepComplete() {
  const navigate = useNavigate()
  const { state } = useOnboarding()
  const authStates = authorizationStatesNeeded(state)
  const authsDone = authStates.every((s) => Boolean(state.implementation.authorizations[s]))

  return (
    <Stack spacing={2.5}>
      <StepCard>
        <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#059669' }}>
          Submission Complete
        </Typography>
        <Typography component="h3" sx={{ mt: 0.5, fontSize: { xs: '1.5rem', sm: '1.75rem' }, fontWeight: 700, color: '#0f172a' }}>
          Congratulations!
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <BodyText>Thank you for completing your onboarding package.</BodyText>
          <BodyText>
            A MISC Onboarding Coordinator will review your submission and schedule an implementation meeting as soon as
            possible.
          </BodyText>
        </Stack>
      </StepCard>

      <StepCard>
        <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b' }}>
          Next Steps
        </Typography>
        <Typography component="h3" sx={{ mt: 0.5, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
          Want to Get Ahead of the Curve?
        </Typography>
        <BodyText sx={{ mt: 1 }}>While your onboarding package is being reviewed, please work on the following:</BodyText>

        <Stack spacing={3} sx={{ mt: 3 }}>
          <Box>
            <SubHeading>If Writing Business</SubHeading>
            <TaskHeading done={Boolean(state.otrUpload)}>Submit a test Open Tax Report (OTR)</TaskHeading>
            <OtrUpload />
          </Box>
          <Box>
            <SubHeading>Optional but Recommended</SubHeading>
            <TaskHeading done={Boolean(state.implementation.signature)}>Provide a quality digital/scanned signature</TaskHeading>
            <SignatureUpload />
          </Box>
          <Box>
            <SubHeading>State-Specific Requirements</SubHeading>
            <TaskHeading done={authStates.length > 0 && authsDone}>Complete third-party filer authorizations</TaskHeading>
            <AuthorizationUploads />
          </Box>
        </Stack>

        <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              markClientOnboardingComplete()
              navigate(AppRoutes.client.home)
            }}
          >
            Return to Dashboard
          </Button>
          <Button type="button" variant="outlined" href={`mailto:${SUPPORT_EMAIL}`}>
            Contact Support
          </Button>
        </Box>
      </StepCard>
    </Stack>
  )
}
